import React from 'react';
import ReactDOM from 'react-dom';
import { Form, Button, Loader, Modal } from 'semantic-ui-react';
import FormInput from './components/form-input';
import DatePicker from './components/date-picker';
import { parseResponse } from './helpers/request';
import { validateReference } from './helpers/referenceNumber';
import { SemanticToastContainer } from 'react-semantic-toasts';
import { LabelPopupIcon } from './components/content/LabelPopupIcon';

class PInvoiceSupplierImport extends React.Component {
    constructor(props) {
        super(props);

        this.inputType = {
            text: 0,
            phone: 1,
            referenceNumber: 2,
            message: 3
        };

        this.emptyValueText = '--';
        this.emptyRow = {
            description: '',
            amount: null,
            amountInputText: '',
            unit: '',
            unitPrice: null,
            unitPriceInputText: '',
            vatPercent: null,
            vatPercentInputText: '',
            sum: null,
            sumText: this.emptyValueText,
            isValid: false
        };

        //Event listeners
        this.handleInputChange = this.handleInputChange.bind(this);
        this.handleRowInputChange = this.handleRowInputChange.bind(this);
        this.handleAddRowClick = this.handleAddRowClick.bind(this);
        this.handleDeleteRowClick = this.handleDeleteRowClick.bind(this);
        this.handleAttachmentChange = this.handleAttachmentChange.bind(this);
        this.handleAttachmentDeleteClick = this.handleAttachmentDeleteClick.bind(this);
        this.handleReadyClick = this.handleReadyClick.bind(this);
        this.handleInvoiceSentModalCloseClick = this.handleInvoiceSentModalCloseClick.bind(this);

        //Helper functions
        this.renderField = this.renderField.bind(this);
        this.renderDoubleField = this.renderDoubleField.bind(this);
        this.renderDateField = this.renderDateField.bind(this);
        this.renderRow = this.renderRow.bind(this);
        this.renderRowField = this.renderRowField.bind(this);
        this.updateTotals = this.updateTotals.bind(this);
        this.renderTotals = this.renderTotals.bind(this);
        this.clearData = this.clearData.bind(this);
        this.hasValue = this.hasValue.bind(this);
        this.hasStringValue = this.hasStringValue.bind(this);
        this.round2decimals = this.round2decimals.bind(this);
        this.parseFloat = this.parseFloat.bind(this);
        this.renderFloat = this.renderFloat.bind(this);
        this.isReady = this.isReady.bind(this);
        this.isLoading = this.isLoading.bind(this);
        this.fileToBase64 = this.fileToBase64.bind(this);

        //State
        this.state = {
            data: {
                billerBusinessIdentifier: '',
                billerName: '',
                billerContactPersonName: '',
                billerPhoneNumber: '',
                iban: '',
                bic: '',
                invoiceNumber: '',
                receiverOrderNumber: '',
                senderOrderNumber: '',
                receiverBusinessIdentifier: '',
                receiverContractNumber: '',
                senderContractNumber: '',
                invoiceDate: '',
                dueDate: '',
                deliveryDate: '',
                referenceNumber: '',
                message: '',
                rows: [
                    { ...this.emptyRow }
                ]
            },
            errors: [],
            ready: false,
            sending: false,
            required: [
                'billerName',
                'billerPhoneNumber',
                'iban',
                'bic',
                'receiverBusinessIdentifier',
                'invoiceNumber',
                'invoiceDate',
                'dueDate',
                'deliveryDate'
            ],
            totals: {
                vatExcluded: 1.0,
                sum: 1.0,
                vats: [{
                    vat: -1,
                    total: 1.0
                }]
            },
            attachments: [{
                name: '',
                file: null
            }],
            parsingAttachment: false,
            invoiceSentModalOpen: false
        };
        this.state.totals = null;
        this.state.attachments = [];

        //Refs
        this.attachmentInputRef = React.createRef();
    }

    componentDidMount() {
        this.updateTotals();
    }
    
    componentWillUnmount() { }

    handleInputChange(propertyName, isRequired, inputType, value) {
        var errors = this.state.errors.filter(errorPropertyName => errorPropertyName !== propertyName);
        if (
            (isRequired && !this.hasStringValue(value)) ||
            (inputType === this.inputType.phone && this.hasStringValue(value) && !(new RegExp('^[0-9 +]*$').test(value))) ||
            (inputType === this.inputType.referenceNumber && this.hasStringValue(value) && !validateReference(value))
        ) {
            errors.push(propertyName);
        }
        this.setState((previousState) => ({
            data: {
                ...previousState.data,
                [propertyName]: value
            },
            errors: errors
        }), () => {
            var ready = this.state.errors.length === 0;
            for (var i = 0; i < this.state.required.length && ready; i++) {
                ready = this.hasValue(this.state.data[this.state.required[i]]);
            }
            this.setState({
                ready: ready
            });
        });
    }

    handleRowInputChange(rowIndex, propertyName, isNumber, isPercent, value) {
        var rows = this.state.data.rows.map(r => r);
        var row = rows[rowIndex];
        if (isNumber) {
            row[propertyName + 'InputText'] = value;
            var number = this.parseFloat(value);
            row[propertyName] = isPercent && number !== null ? Math.max(0, Math.min(100, number)) : number;

            //Update sum
            row.isValid = row.amount !== null && row.unitPrice !== null && row.vatPercent !== null;
            if (row.isValid) {
                row.sum = this.round2decimals(row.amount * row.unitPrice * (1 + row.vatPercent / 100));
                row.sumText = this.renderFloat(row.sum);
            } else {
                row.sum = null;
                row.sumText = this.emptyValueText;
            }
        } else {
            row[propertyName] = value;
        }
        this.setState((previousState) => ({
            data: {
                ...previousState.data,
                rows: rows
            }
        }), () => {
            this.updateTotals();
        });
    }

    handleAddRowClick(rowIndex) {
        var rows = this.state.data.rows;
        rows.splice(rowIndex + 1, 0, {
            ...this.emptyRow
        });
        this.setState((previousState) => ({
            data: {
                ...previousState.data,
                rows: rows
            }
        }), () => {
            this.updateTotals();
        });
    }

    handleDeleteRowClick(rowIndex) {
        var rows = this.state.data.rows;
        rows.splice(rowIndex, 1);
        this.setState((previousState) => ({
            data: {
                ...previousState.data,
                rows: rows
            }
        }), () => {
            this.updateTotals();
        });
    }

    handleAttachmentChange(event) {
        if (!this.state.parsingAttachment) {
            this.setState({
                parsingAttachment: true
            }, async () => {
                var base64 = await this.fileToBase64(event.target.files[0]);
                var attachments = this.state.attachments;
                attachments.push({
                    name: event.target.files[0].name,
                    file: base64
                });
                this.setState({
                    attachments: attachments,
                    parsingAttachment: false
                }, () => {
                    this.attachmentInputRef.current.value = '';
                });
            });
        }
    }

    handleAttachmentDeleteClick(index) {
        var attachments = [];
        for (var i = 0; i < this.state.attachments.length; i++) {
            if (i !== index) {
                attachments.push(this.state.attachments[i]);
            }
        }
        this.setState({
            attachments: attachments
        });
    }

    handleReadyClick() {
        if (this.isReady()) {
            this.setState({
                sending: true
            }, () => {
                fetch(globalParameters.rootUrl + 'PInvoice/Supplier/Import/Send', {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        data: {
                            ...this.state.data,
                            rows: this.state.data.rows.filter(row => row.isValid),
                            attachments: this.state.attachments.map((attachment) => {
                                return {
                                    name: attachment.name,
                                    base64data: attachment.file
                                };
                            })
                        }
                    })
                })
                    .then(response => {
                        return parseResponse(response, {
                            logErrorMessage: 'Failed send invoice.',
                            isBooleanCheck: true,
                            responseErrorMessageProperty: 'message',
                            simpleToastErrorMessage: true
                        });
                    })
                    .then(result => {
                        if (result) {
                            //Success
                            this.setState({
                                sending: false,
                                invoiceSentModalOpen: true
                            }, () => {
                                this.clearData();
                            });
                        } else {
                            //Error
                            this.setState({
                                sending: false
                            });
                        }
                    });
            });
        }
    }

    handleInvoiceSentModalCloseClick() {
        this.setState({
            invoiceSentModalOpen: false
        });
    }

    renderField(label, placeholder, propertyName, inputType) {
        var isRequired = this.state.required.indexOf(propertyName) !== -1;
        var value = typeof this.state.data[propertyName] === 'string' ? this.state.data[propertyName] : '';
        var error = this.state.errors.indexOf(propertyName) !== -1;
        inputType = inputType ?? this.inputType.text;
        var disabled = this.isLoading() || (inputType === this.inputType.referenceNumber && this.hasValue(this.state.data.message)) ||
            (inputType === this.inputType.message && this.hasValue(this.state.data.referenceNumber))
        return (
            <Form.Field className={error ? 'error' : ''}>
                <label style={disabled ? { opacity: '0.45' } : undefined}>{label + (isRequired ? ' *' : '')}
                    {
                        inputType === this.inputType.referenceNumber ?
                            <LabelPopupIcon content={localization.PInvoiceSupplierImportReferenceHelp} />
                            : null
                    }
                </label>
                <input
                    type="text"
                    value={value}
                    placeholder={placeholder ?? label}
                    disabled={disabled}
                    onChange={(event) => { this.handleInputChange(propertyName, isRequired, inputType, event.target.value); }}
                />
            </Form.Field>
        );
    }

    renderDoubleField(label, placeholder1, placeholder2, propertyName1, propertyName2) {
        var isRequired = this.state.required.indexOf(propertyName1) !== -1 || this.state.required.indexOf(propertyName2) !== -1;
        var value1 = typeof this.state.data[propertyName1] === 'string' ? this.state.data[propertyName1] : '';
        var value2 = typeof this.state.data[propertyName2] === 'string' ? this.state.data[propertyName2] : '';
        var error1 = this.state.errors.indexOf(propertyName1) !== -1;
        var error2 = this.state.errors.indexOf(propertyName2) !== -1;
        return (
            <Form.Field>
                <label style={this.isLoading() ? { opacity: '0.45' } : undefined}>{label + (isRequired ? ' *' : '')}</label>
                <div className="two fields">
                    <Form.Field className={error1 ? 'error' : ''}>
                        <input
                            type="text"
                            value={value1}
                            placeholder={placeholder1}
                            onChange={(event) => { this.handleInputChange(propertyName1, isRequired, this.inputType.text, event.target.value); }}
                            disabled={this.isLoading()}
                        />
                    </Form.Field>
                    <Form.Field className={error2 ? 'error' : ''}>
                        <input
                            type="text"
                            value={value2}
                            placeholder={placeholder2}
                            onChange={(event) => { this.handleInputChange(propertyName2, isRequired, this.inputType.text, event.target.value); }}
                            disabled={this.isLoading()}
                        />
                    </Form.Field>
                </div>
            </Form.Field>
        );
    }

    renderDateField(label, placeholder, propertyName) {
        var isRequired = this.state.required.indexOf(propertyName) !== -1;
        var value = typeof this.state.data[propertyName] === 'string' ? this.state.data[propertyName] : '';
        var error = this.state.errors.indexOf(propertyName) !== -1;
        return (
            <FormInput
                control={DatePicker}
                label={label + (isRequired ? ' *' : '')}
                autoComplete="off"
                onChange={(event, data) => { this.handleInputChange(propertyName, isRequired, this.inputType.text, data.value); }}
                value={value}
                placeholder={placeholder ?? label}
                className={error ? 'error' : ''}
                disabled={this.isLoading()}
            />
        );
    }

    renderRow(row, index) {
        return (
            <div className="fields" style={{ paddingBottom: '14px', borderBottom: '1px solid rgba(34, 36, 38, 0.15)' }}>
                <div className="four wide field">
                    {this.renderRowField(row, index, localization.PInvoiceSupplierInvoiceRowDescription, index === 0, 'description', false, false)}
                </div>
                <div className="two wide field">
                    {this.renderRowField(row, index, localization.PInvoiceSupplierInvoiceRowAmount, index === 0, 'amount', true, false)}
                </div>
                <div className="two wide field">
                    {this.renderRowField(row, index, localization.Unit, index === 0, 'unit', false, false)}
                </div>
                <div className="two wide field">
                    {this.renderRowField(row, index, localization.APrice, index === 0, 'unitPrice', true, false)}
                </div>
                <div className="two wide field">
                    {this.renderRowField(row, index, localization.VatPercent, index === 0, 'vatPercent', true, true)}
                </div>
                <div className="two wide field" style={{ textAlign: 'right' }}>
                    {index === 0 ? <label>{localization.PInvoiceSupplierInvoiceRowTotal}</label> : null}
                    <span style={{ lineHeight: '38px' }}>
                        {row.sumText}
                    </span>
                </div>
                <div className="two wide field" style={{ textAlign: 'right' }}>
                    <span style={{ lineHeight: index === 0 ? '68px' : '38px' }}>
                        {
                            this.state.data.rows.length > 1 ?
                                <i
                                    className="large red minus circle link icon"
                                    onClick={() => { this.handleDeleteRowClick(index); }}
                                    style={this.isLoading() ? { display: 'none' } : undefined}
                                ></i> : null
                        }
                        <i
                            className="large green plus circle link icon"
                            onClick={() => { this.handleAddRowClick(index); }}
                            style={this.isLoading() ? { display: 'none' } : undefined}
                        ></i>
                    </span>
                </div>
            </div>
        );
    }

    renderRowField(row, index, label, showLabel, propertyName, isNumber, isPercent) {
        return (
            <>
                {showLabel ? <label>{label}</label> : null}
                <input
                    type="text"
                    value={isNumber ? row[propertyName + 'InputText'] : row[propertyName]}
                    placeholder={label}
                    onChange={(event) => { this.handleRowInputChange(index, propertyName, isNumber, isPercent, event.target.value); }}
                    disabled={this.isLoading()}
                />
            </>
        );
    }

    updateTotals() {
        var validRows = this.state.data.rows.filter(row => row.isValid);
        if (validRows.length === 0) {
            //No valid rows, clear totals data
            this.setState({
                totals: null
            });
        } else {
            var sum = 0.0;
            var vatExcluded = 0.0;
            var vats = [];
            for (var i = 0; i < validRows.length; i++) {
                var row = validRows[i];
                sum += row.sum;
                var rowVatExcluded = this.round2decimals(row.amount * row.unitPrice);
                vatExcluded += rowVatExcluded;
                var vatAmount = this.round2decimals(rowVatExcluded * (row.vatPercent / 100));
                var vatTotal = vats.find(v => v.vat === row.vatPercent);
                if (vatTotal !== undefined) {
                    vatTotal.total += vatAmount;
                } else {
                    vats.push({
                        vat: row.vatPercent,
                        total: vatAmount
                    });
                }
            }
            this.setState({
                totals: {
                    vatExcluded: vatExcluded,
                    sum: sum,
                    vats: vats
                }
            });
        }
    }

    renderTotals() {
        if (this.state.totals === null) {
            //No data
            return (
                <div className="ui grid" style={{ margin: '0px' }}>
                    <div className="row">
                        <div className="twelve wide column">
                            <b>{localization.VAT.toUpperCase() + ':'}</b>
                        </div>
                        <div className="four wide column">
                            <b>{this.emptyValueText}</b>
                        </div>
                    </div>
                    <div className="row">
                        <div className="twelve wide column">
                            <b>{localization.Total + ':'}</b>
                        </div>
                        <div className="four wide column">
                            <b>{this.emptyValueText}</b>
                        </div>
                    </div>
                </div>
            );
        }
        return (
            <div className="ui grid" style={{ margin: '0px' }}>
                <div className="row">
                    <div className="twelve wide column">
                        <b>{localization.VatExcludedAmount + ':'}</b>
                    </div>
                    <div className="four wide column">
                        <b>{this.renderFloat(this.state.totals.vatExcluded)}</b>
                    </div>
                </div>
                {this.state.totals.vats.map((vatData) => {
                    return (
                        <div className="row">
                            <div className="twelve wide column">
                                <b>{localization.VAT.toUpperCase() + ' ' + this.renderFloat(vatData.vat) + '%:'}</b>
                            </div>
                            <div className="four wide column">
                                <b>{this.renderFloat(vatData.total)}</b>
                            </div>
                        </div>
                    );
                })}
                <div className="row">
                    <div className="twelve wide column">
                        <b>{localization.Total + ':'}</b>
                    </div>
                    <div className="four wide column">
                        <b>{this.renderFloat(this.state.totals.sum)}</b>
                    </div>
                </div>
            </div>
        );
    }

    clearData() {
        this.setState((previousState) => ({
            data: {
                ...previousState.data,
                billerBusinessIdentifier: '',
                billerName: '',
                billerContactPersonName: '',
                billerPhoneNumber: '',
                iban: '',
                bic: '',
                invoiceNumber: '',
                receiverOrderNumber: '',
                senderOrderNumber: '',
                receiverBusinessIdentifier: '',
                receiverContractNumber: '',
                senderContractNumber: '',
                invoiceDate: '',
                dueDate: '',
                deliveryDate: '',
                referenceNumber: '',
                message: '',
                rows: [
                    { ...this.emptyRow }
                ]
            },
            errors: [],
            attachments: [],
            ready: false
        }), () => {
            this.updateTotals();
        });
    }

    hasValue(p) {
        return p !== undefined && p !== null && p !== '';
    }

    hasStringValue(p) {
        return typeof p === 'string' && p.length !== 0;
    }

    round2decimals(float) {
        return Math.round(float * 100) / 100;
    }

    parseFloat(string) {
        if (!this.hasValue(string)) {
            return null;
        }
        string = (string + '').replace(',', '.').replace(/\s/g, '').replace('−', '-');
        var number = Number(string);
        return Number.isNaN(number) ? null : number;
    }

    renderFloat(float) {
        return float.toFixed(2).replace('.', ',');
    }

    isReady() {
        return this.state.ready && !this.state.sending && this.state.errors.length === 0 && this.state.data.rows.filter(row => row.isValid).length > 0;
    }

    isLoading() {
        return this.state.sending;
    }

    fileToBase64(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
        });
    }

    render() {
        return (
            <div className="ag-background-gradient" style={{ height: '100%', margin: '0px', paddingLeft: '28px', paddingRight: '28px', paddingTop: '14px', paddingBottom: '14px', overflowY: 'auto', position: 'relative' }}>
                <div className="ui one column grid ag-top-header-grid">
                    <div className="column">
                        <h2 className="ui center aligned header ag-topheader">
                            <div className="content ag-topheader-content">
                                {localization.PInvoiceSupplierImportTitle}
                            </div>
                        </h2>
                    </div>
                </div>
                <div className="pi-supplier-import-language">
                    <a href={globalParameters.rootUrl + 'PInvoice/Supplier/Import?lang=fi'}>FI</a>/
                    <a href={globalParameters.rootUrl + 'PInvoice/Supplier/Import?lang=sv'}>SV</a>/
                    <a href={globalParameters.rootUrl + 'PInvoice/Supplier/Import?lang=en'}>EN</a>
                </div>
                <div className="ag-maxwidth-container" style={{ maxWidth: '933px' }}>
                    <div className="ag-content-box">
                        <Form>
                            {this.isLoading() ? <div style={{ position: 'absolute', width: '100%', top: '20px' }}><Loader active inline className="large centered" /></div> : null}
                            <div className="ui stackable grid">
                                <div className="eight wide column">
                                    <h3 className="ui dividing header">
                                        <div className="content">
                                            {localization.Biller}
                                        </div>
                                    </h3>
                                    {this.renderField(localization.BusinessIdentifier, localization.PISupplierBusinessIdentifierPlaceholder, 'billerBusinessIdentifier')}
                                    {this.renderField(localization.BillerName, null, 'billerName')}
                                    {this.renderField(localization.ContactPersonName, localization.ContactPerson, 'billerContactPersonName')}
                                    {this.renderField(localization.BillerPhone, localization.PhoneNumber, 'billerPhoneNumber', this.inputType.phone)}
                                    {this.renderDoubleField(localization.BankAccount, 'IBAN', 'BIC', 'iban', 'bic')}
                                    <h3 className="ui dividing header">
                                        <div className="content">
                                            {localization.Receiver}
                                        </div>
                                    </h3>
                                    {this.renderField(localization.BusinessIdentifier, null, 'receiverBusinessIdentifier')}
                                </div>
                                <div className="eight wide column">
                                    <h3 className="ui dividing header">
                                        <div className="content">
                                            {localization.PInvoiceSupplierInvoiceHeader}
                                        </div>
                                    </h3>
                                    {this.renderField(localization.PInvoiceSupplierInvoiceInvoiceNumber, null, 'invoiceNumber')}
                                    {this.renderField(localization.ReceiversOrderNumber, localization.YourOrderNumber, 'receiverOrderNumber')}
                                    {this.renderField(localization.SendersOrderNumber, localization.YourOrderNumber, 'senderOrderNumber')}
                                    {this.renderField(localization.ReceiverContractNumber, localization.YourContractNumber, 'receiverContractNumber')}
                                    {this.renderField(localization.SenderContractNumber, localization.YourContractNumber, 'senderContractNumber')}
                                    {this.renderDateField(localization.InvoiceDateLong, null, 'invoiceDate')}
                                    {this.renderDateField(localization.DueDate, null, 'dueDate')}
                                    {this.renderDateField(localization.DeliveryDateLong, null, 'deliveryDate')}
                                    {this.renderField(localization.PaymentReference, localization.BankReference, 'referenceNumber', this.inputType.referenceNumber)}
                                    <div className="pi-supplier-import-language-reference-or-text">{localization.Or.toUpperCase()}</div>
                                    {this.renderField(localization.Message, null, 'message', this.inputType.message)}
                                </div>
                                <div className="sixteen wide column">
                                    <h3 className="ui dividing header">
                                        <div className="content">
                                            {localization.InvoiceRows2}
                                        </div>
                                    </h3>
                                    {this.state.data.rows.map(this.renderRow)}
                                </div>
                                <div className="sixteen wide column">
                                    {this.renderTotals()}
                                </div>
                                <div className="sixteen wide column">
                                    <h3 className="ui dividing header">
                                        <div className="content">
                                            {localization.Attachments}
                                        </div>
                                    </h3>
                                    <div className="ui list">
                                        {this.state.attachments.map((attachment, index) => {
                                            return (
                                                <div className="item">
                                                    {attachment.name}
                                                    <i className="close link icon" style={{ marginLeft: '5px' }} onClick={() => { this.handleAttachmentDeleteClick(index); }}></i>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <label className="pi-supplier-import-attachment-button">
                                        <div className={'ui button circular piButton' + (this.state.parsingAttachment ? ' disabled' : '')}>
                                            <i className="plus icon"></i>
                                            {localization.Add}
                                        </div>
                                        <input type="file" accept="application/pdf" onChange={this.handleAttachmentChange} ref={this.attachmentInputRef} />
                                    </label>
                                </div>
                                <div className="sixteen wide column">
                                    <Button
                                        className="circular right floated piButton"
                                        disabled={!this.isReady()}
                                        onClick={this.handleReadyClick}
                                    >
                                        <i className="green check icon"></i>
                                        {localization.Ready}
                                    </Button>
                                </div>
                            </div>
                        </Form>
                    </div>
                </div>
                <div className="receipt-upload-toast">
                    <SemanticToastContainer time={200} />
                </div>
                <Modal size={'mini'} open={this.state.invoiceSentModalOpen} onClose={this.handleInvoiceSentModalCloseClick}>
                    <Modal.Content>
                        {localization.SupplierInvoiceSentMessage}
                    </Modal.Content>
                    <Modal.Actions>
                        <Button className="ui circular button piButton" onClick={this.handleInvoiceSentModalCloseClick}>
                            <i className="green check icon"></i>
                            {localization.SendNewInvoice}
                        </Button>
                    </Modal.Actions>
                </Modal>
            </div>
        );
    }
}

export default PInvoiceSupplierImport

ReactDOM.render(<PInvoiceSupplierImport />, document.getElementById('root'));